import { create } from 'zustand';

import type { DeviceCommand } from './types';

/**
 * In-flight remote commands, keyed by device id (story 101849). Holds only commands that
 * are still resolving (Queued / Delivered); a command is dropped the moment it reaches a
 * terminal state (Executed / Failed) or is cancelled. UI state only — the authoritative
 * lifecycle lives on the backend and streams over the `command-ack` channel.
 *
 * One shared store (vs. per-component state) lets the card "Queued" pill, the power
 * control, and the detail "Queued" banner read one source, and keeps a command visible
 * after the grower queues it from a card and navigates into the detail page.
 */
type CommandStoreState = {
  commands: Record<string, DeviceCommand>;
  /** Record/replace the in-flight command for its device. */
  setCommand: (command: DeviceCommand) => void;
  /** Drop the device's in-flight command (resolved or cancelled). */
  clearCommand: (deviceId: string) => void;
};

export const useCommandStore = create<CommandStoreState>((set) => ({
  commands: {},
  setCommand: (command) =>
    set((s) => ({ commands: { ...s.commands, [command.deviceId]: command } })),
  clearCommand: (deviceId) =>
    set((s) => {
      if (!s.commands[deviceId]) return s;
      const next = { ...s.commands };
      delete next[deviceId];
      return { commands: next };
    }),
}));

/** The device's current in-flight command, or null. */
export function useDeviceCommand(deviceId: string): DeviceCommand | null {
  return useCommandStore((s) => s.commands[deviceId] ?? null);
}
